/**
 * Lightweight, swappable toast/confirm notifier
 * ES6 class-based implementation
 *
 * Usage (ES6 module pattern - recommended):
 *   import { notifier } from './core/notifier.js';
 *   notifier.success('Saved!')
 *   notifier.error('Something went wrong', { duration: 6000 })
 *   await notifier.confirm('Are you sure?')
 *   await notifier.fromResponse(fetchResponse, 'Request failed')
 *
 * Usage (Static class methods):
 *   import { Notifier } from './core/notifier.js';
 *   Notifier.showToast('success', 'Saved!')
 *   await Notifier.confirm('Are you sure?')
 *
 * Adapter pattern (optional):
 *   notifier.setAdapter({
 *     showToast(type, message, options),
 *     confirm(message, options) => Promise<boolean>
 *   })
 */

import { TaskScheduler } from "../task-scheduler";
import { translator } from "../../translation/translator";
import { phpDict, phpTranslate } from "../php-data-access";
import { getDefaultView, getParentView } from "../view";
import { UISymbols } from "../../constants/ui-symbols";
import { FormBase } from "../../util/form-base-form";
import { NotifierListener } from "./notifier-listener";

class NotifierFormBase extends FormBase {
    public getContainer(root: ParentNode, selector: string): HTMLElement | null {
        return this.queryElement(root, selector, HTMLElement);
    }
}

const notifierFormBase = new NotifierFormBase();

type NotifierToastType = "success" | "info" | "warn" | "error";
type NotifierPosition = "top-right" | "top-left" | "bottom-right" | "bottom-left";

type TranslationMap = Record<string, string>;
type TranslationDictionary = Record<string, TranslationMap>;
type ResponseErrorValue = string | number | boolean | null | undefined | string[] | ResponseErrorMap;
interface ResponseErrorMap {
    [key: string]: ResponseErrorValue;
}

interface ToastOptions {
    position?: string;
    duration?: number;
    translateKey?: string;
}

interface ConfirmOptions {
    translateKey?: string;
    cancelTranslateKey?: string;
    cancelText?: string;
    okTranslateKey?: string;
    okText?: string;
}

interface PromptOptions {
    cancelText?: string;
    okText?: string;
}

interface NotifierAdapter {
    showToast?: (type: NotifierToastType, message: string, options?: ToastOptions) => HTMLElement;
    confirm?: (message: string, options?: ConfirmOptions) => Promise<boolean>;
    prompt?: (message: string, defaultValue?: string, options?: PromptOptions) => Promise<string | null>;
}

interface NotifierResponseData {
    message?: string;
    error?: string;
    errors?: ResponseErrorMap | string[] | string | null;
}

/**
 * Notifier class for handling toast notifications and confirm dialogs
 */
export class Notifier {
    public static DEFAULT_DURATION = 2000;
    public static POSITIONS: NotifierPosition[] = ["top-right", "top-left", "bottom-right", "bottom-left"];

    private _adapter: NotifierAdapter | null;

    public constructor() {
        this._adapter = null;
    }

    public static getContainer(position: string): HTMLElement {
        const pos = this.POSITIONS.includes(position as NotifierPosition)
            ? (position as NotifierPosition)
            : "top-right";
        const className = `nf-container nf-${pos.replace(/\s+/g, "-")}`;
        let el = notifierFormBase.getContainer(document, `.${className.split(" ").join(".")}`);
        if (!el) {
            el = document.createElement("div");
            el.className = className;
            document.body.appendChild(el);
        }
        if (!(el instanceof HTMLElement)) {
            const fallback = document.createElement("div");
            fallback.className = className;
            document.body.appendChild(fallback);
            return fallback;
        }
        return el;
    }

    public static closeToast(el: HTMLElement | null | undefined): void {
        if (!el) return;
        el.style.opacity = "0";
        el.style.transform = "translateY(-4px)";
        TaskScheduler.schedule(() => el.remove(), "raf");
    }

    public static translateMessage(translateKey: string, fallbackMessage = ""): string {
        const displayMessage = String(fallbackMessage || translateKey || "");

        if (translator && typeof translateKey === "string") {
            const translated = translator.getTranslation(translateKey);
            if (translated && translated !== translateKey) {
                return translated;
            }
        }

        if (displayMessage === translateKey && typeof translateKey === "string") {
            const currentLang = translator?.getCurrentLanguage() || "th";
            const combinedTranslations: TranslationDictionary = {
                ...(phpDict("Navigation") || {}),
                ...(phpDict("Boards") || {}),
                ...(phpDict("Products") || {}),
                ...(phpDict("Users") || {}),
                ...(phpDict("UI") || {}),
                ...(phpDict("Comments") || {}),
                ...(phpDict("Posts") || {}),
                ...(phpDict("Attachments") || {}),
                ...(phpDict("Modals") || {}),
                ...(phpDict("FormValidation") || {}),
            };

            const translationSet = combinedTranslations[translateKey];
            if (translationSet) {
                const directTranslated =
                    translationSet[currentLang] || translationSet.th || translationSet.en || translateKey;
                if (directTranslated !== translateKey) {
                    return directTranslated;
                }
            }
        }

        if (displayMessage === translateKey && typeof translateKey === "string") {
            const currentLang = translator?.getCurrentLanguage() || "th";
            const fallbackTranslated = phpTranslate(translateKey, currentLang);
            if (fallbackTranslated) {
                return fallbackTranslated;
            }
        }

        if (displayMessage === translateKey && typeof translateKey === "string") {
            if (translator && !translator.translations[translateKey]) {
                translator.loadTranslations();

                const refreshedTranslation = translator.getTranslation(translateKey);
                if (refreshedTranslation && refreshedTranslation !== translateKey) {
                    return refreshedTranslation;
                }
            }

            const parentWindow = getParentView();
            const selfView = getDefaultView();
            if (displayMessage === translateKey && parentWindow && selfView && parentWindow !== selfView) {
                try {
                    const dataTranslater = Reflect.get(parentWindow, "DataTranslater");
                    const getTranslation =
                        dataTranslater && typeof dataTranslater === "object"
                            ? Reflect.get(dataTranslater, "getTranslation")
                            : null;
                    const parentTranslation =
                        typeof getTranslation === "function" ? getTranslation.call(dataTranslater, translateKey) : null;
                    if (typeof parentTranslation === "string" && parentTranslation !== translateKey) {
                        return parentTranslation;
                    }
                } catch (e) {
                    console.error("Notifier.translateMessage error:", e);
                }
            }
        }

        return displayMessage;
    }

    public static showToast(type: NotifierToastType, message: string, options: ToastOptions = {}): HTMLElement {
        const pos = options.position || "top-right";
        const duration = typeof options.duration === "number" ? options.duration : Notifier.DEFAULT_DURATION;
        const container = Notifier.getContainer(pos);

        const translateKey = options.translateKey || message;
        const displayMessage = Notifier.translateMessage(translateKey, message);

        const toast = document.createElement("div");
        toast.className = `nf-toast nf-toast-${type}`;
        toast.setAttribute("role", type === "error" || type === "warn" ? "alert" : "status");
        toast.setAttribute("aria-live", "polite");

        const row = document.createElement("div");
        row.className = "nf-row";
        const span = document.createElement("span");
        span.textContent = displayMessage;
        const btn = document.createElement("button");
        btn.className = "nf-toast-close";
        btn.setAttribute("aria-label", "Close");
        btn.textContent = UISymbols.PICKER_REMOVE;
        NotifierListener.bindClick(btn, () => Notifier.closeToast(toast));
        row.appendChild(span);
        row.appendChild(btn);
        toast.appendChild(row);

        container.appendChild(toast);
        if (duration > 0) setTimeout(() => Notifier.closeToast(toast), duration);
        return toast;
    }

    public static getButtonText(key: string, defaultText: string): string {
        if (translator) {
            const translated = translator.getTranslation(key);
            if (translated && translated !== key) {
                return translated;
            }
        }

        const currentLang = translator?.getCurrentLanguage() || "th";
        const dictUI = phpDict("UI") as TranslationDictionary | null;
        const dictTranslation = dictUI?.[key];
        if (dictTranslation) {
            return dictTranslation[currentLang] || dictTranslation.th || dictTranslation.en || defaultText;
        }

        return defaultText;
    }

    public static confirm(message: string, options: ConfirmOptions = {}): Promise<boolean> {
        return new Promise((resolve) => {
            const backdrop = document.createElement("div");
            backdrop.className = "nf-confirm-backdrop";
            const modal = document.createElement("div");
            modal.className = "nf-confirm";
            const msg = document.createElement("div");
            msg.className = "nf-confirm-message";

            const translateKey = options.translateKey || message;
            let displayMessage = Notifier.translateMessage(translateKey, message);

            if (displayMessage === translateKey || displayMessage === message) {
                switch (translateKey) {
                    case "confirm_delete_post":
                    case "posts_delete_confirm":
                        displayMessage = "คุณแน่ใจหรือไม่ที่จะลบโพสต์นี้";
                        break;
                    case "comments_delete_confirm":
                        displayMessage = "คุณแน่ใจหรือไม่ว่าต้องการลบความคิดเห็นนี้";
                        break;
                    default:
                        displayMessage = String(message || "Are you sure?");
                }
            }

            msg.textContent = displayMessage;

            const actions = document.createElement("div");
            actions.className = "nf-confirm-actions";

            const btnCancel = document.createElement("button");
            const cancelKey = options.cancelTranslateKey || "cancel";
            const cancelSemanticClass = cancelKey === "cancel" ? " cancel" : "";
            btnCancel.className = `nf-btn nf-btn-secondary${cancelSemanticClass}`;
            btnCancel.textContent = Notifier.getButtonText(cancelKey, options.cancelText || "Cancel");

            const btnOk = document.createElement("button");
            const okKey = options.okTranslateKey || "delete";
            const okSemanticClass = okKey === "delete" ? " delete" : "";
            btnOk.className = `nf-btn nf-btn-primary${okSemanticClass}`;
            btnOk.textContent = Notifier.getButtonText(okKey, options.okText || "OK");

            const closeDialog = (result: boolean) => {
                backdrop.remove();
                resolve(result);
            };

            NotifierListener.bindClick(btnCancel, () => closeDialog(false));
            NotifierListener.bindClick(btnOk, () => closeDialog(true));
            NotifierListener.bindClick(backdrop, (e) => {
                if (e.target === backdrop) {
                    closeDialog(false);
                }
            });

            actions.appendChild(btnCancel);
            actions.appendChild(btnOk);
            modal.appendChild(msg);
            modal.appendChild(actions);
            backdrop.appendChild(modal);
            document.body.appendChild(backdrop);
        });
    }

    public static prompt(message: string, defaultValue = "", options: PromptOptions = {}): Promise<string | null> {
        return new Promise((resolve) => {
            const backdrop = document.createElement("div");
            backdrop.className = "nf-confirm-backdrop";
            const modal = document.createElement("div");
            modal.className = "nf-confirm";
            const msg = document.createElement("div");
            msg.className = "nf-confirm-message";
            msg.textContent = String(message || "Please enter a value:");

            const input = document.createElement("input");
            input.type = "text";
            input.value = defaultValue;
            input.style.cssText =
                "width: 100%; padding: 8px; margin: 8px 0; border: 1px solid #ccc; border-radius: 4px; font-size: 14px;";

            const actions = document.createElement("div");
            actions.className = "nf-confirm-actions";
            const btnCancel = document.createElement("button");
            btnCancel.className = "nf-btn nf-btn-secondary";
            btnCancel.textContent = options.cancelText || "Cancel";
            const btnOk = document.createElement("button");
            btnOk.className = "nf-btn nf-btn-primary";
            btnOk.textContent = options.okText || "OK";

            NotifierListener.bindClick(btnCancel, () => {
                backdrop.remove();
                resolve(null);
            });
            NotifierListener.bindClick(btnOk, () => {
                const value = input.value.trim();
                backdrop.remove();
                resolve(value || null);
            });
            NotifierListener.bindClick(backdrop, (e) => {
                if (e.target === backdrop) {
                    backdrop.remove();
                    resolve(null);
                }
            });

            actions.appendChild(btnCancel);
            actions.appendChild(btnOk);
            modal.appendChild(msg);
            modal.appendChild(input);
            modal.appendChild(actions);
            backdrop.appendChild(modal);
            document.body.appendChild(backdrop);

            TaskScheduler.schedule(() => input.focus(), "raf");
        });
    }

    public static async fromResponse(response: Response, fallbackMessage = "Request failed"): Promise<string> {
        try {
            const ct = response.headers?.get?.("content-type") || "";
            if (ct.includes("application/json")) {
                const data = (await response.json()) as NotifierResponseData;

                const errors = data?.errors;
                let errorText = "";
                if (Array.isArray(errors)) {
                    errorText = errors.map((message) => String(message)).join("\n");
                } else if (errors && typeof errors === "object") {
                    const lines: string[] = [];
                    for (const [field, messages] of Object.entries(errors as ResponseErrorMap)) {
                        if (Array.isArray(messages)) {
                            for (const entry of messages) {
                                lines.push(`${field}: ${String(entry)}`);
                            }
                        } else if (messages !== undefined && messages !== null) {
                            lines.push(`${field}: ${String(messages)}`);
                        }
                    }
                    errorText = lines.join("\n");
                }

                const msg = data?.message || data?.error || errorText || fallbackMessage;
                this.showToast("error", msg);
                return msg;
            }
            const text = await response.text();
            const msg = text?.slice(0, 300) || `${fallbackMessage} (status ${response.status})`;
            this.showToast("error", msg);
            return msg;
        } catch (e) {
            console.error("Notifier.fromResponse error:", e);
            const msg = fallbackMessage || "Request failed";
            this.showToast("error", msg);
            return msg;
        }
    }

    public setAdapter(adapter: NotifierAdapter | null): void {
        if (adapter && typeof adapter.showToast === "function") {
            this._adapter = adapter;
        }
    }

    public toast(type: NotifierToastType, message: string, options?: ToastOptions): void {
        const showToast = this._adapter?.showToast || Notifier.showToast;
        showToast(type, message, options);
    }

    public success(msg: string, options?: ToastOptions): void {
        this.toast("success", msg, options);
    }

    public info(msg: string, options?: ToastOptions): void {
        this.toast("info", msg, options);
    }

    public warn(msg: string, options?: ToastOptions): void {
        const displayMessage = Notifier.translateMessage(options?.translateKey || msg, msg);
        console.warn("[Notifier Warning]", displayMessage, options || "");
        this.toast("warn", msg, options);
    }

    public error(msg: string, options?: ToastOptions): void {
        const displayMessage = Notifier.translateMessage(options?.translateKey || msg, msg);
        console.error("[Notifier Error]", displayMessage, options || "");
        this.toast("error", msg, options);
    }

    public confirm(message: string, options?: ConfirmOptions): Promise<boolean> {
        const confirmFn = this._adapter?.confirm || Notifier.confirm;
        return confirmFn(message, options);
    }

    public prompt(message: string, defaultValue?: string, options?: PromptOptions): Promise<string | null> {
        const promptFn = this._adapter?.prompt || Notifier.prompt;
        return promptFn(message, defaultValue, options);
    }

    public fromResponse(response: Response, fallbackMessage?: string): Promise<string> {
        return Notifier.fromResponse(response, fallbackMessage);
    }

    public test(): void {
        this.success("Test success message");
        this.info("Test info message");
        this.warn("Test warning message");
        this.error("Test error message");
    }
}

export default Notifier;

const notifier = new Notifier();

export { notifier };
